import { BillingRecipients, User } from '@/types';
import { Flex, Tag, Typography } from 'antd';
import {
  InfoCircleOutlined,
  MailOutlined,
  StarFilled,
  SyncOutlined,
  WarningFilled,
} from '@ant-design/icons';
import './index.scss';

interface Props {
  // Used only to mark the Primary contact — not to decide who is listed.
  billingAddress?: string;
  // Who actually receives the invoices, as resolved server-side by
  // StripeService.resolveFranchiseBillingRecipients (the same resolver the
  // invoice sender uses). Displayed as-is: this card must never re-derive the
  // billingContacts → billingAddress → activatedBy chain (PR review P0).
  billingRecipients?: BillingRecipients;
  users?: Partial<User>[];
  // True when the Stripe customer email is known to be out of sync with
  // billingAddress (franchise.stripeBillingEmailSynced === false) — surfaced
  // so support doesn't trust "Primary" as the live Stripe email during the
  // (usually short) window before the daily reconciler closes the gap.
  stripeBillingEmailSynced?: boolean;
  // PR #805 finding 1: getAdminFranchisorById's error path always throws
  // (its `return null` after handleError is dead code — handleError is typed
  // `never`), so a failed/loading fetch leaves `franchise` as `undefined`,
  // indistinguishable from "no billing contacts" unless the caller tells us
  // which state we're in.
  isLoading?: boolean;
  isError?: boolean;
}

// Matching is case-insensitive: the backend stores contacts lowercased while
// account emails may carry mixed case (mirrors currentBillingContacts /
// assertEmailsAreFranchiseAccounts on the backend).
const normalize = (email?: string) => (email ?? '').trim().toLowerCase();

// TCX-875: standalone card (not a Collapse panel row) rendered in the wide
// content column, not inside .detailed-wrapper — so it can't reuse that
// file's .details-section-line/.general-info-value classes (those are
// scoped under .detailed-content and silently no-op here). Uses its own
// .billing-contact-row classes instead (see index.scss).
export default function FranchisorBillingContactsAdminSection({
  billingAddress,
  billingRecipients,
  users,
  stripeBillingEmailSynced,
  isLoading,
  isError,
}: Props) {
  const contacts = billingRecipients?.emails ?? [];
  const isOwnerFallback = billingRecipients?.source === 'activatedBy';

  // Two distinct account states, both matched case-insensitively:
  // - activeAccountEmails: a live franchise user — matching this is a clean
  //   contact.
  // - deletedAccountEmails: a franchise user whose account was later
  //   soft-deleted. The backend's assertEmailsAreFranchiseAccounts (and
  //   resolveBillingContactEmails, which actually sends the invoices) does
  //   NOT exclude these — the account still exists and still receives mail —
  //   so this is deliberately stricter than the backend, not a mirror of it.
  //   It gets its own tag rather than being folded into "not a franchise
  //   account", which is reserved for an email that never was one (the
  //   TCX-873 condition).
  // isDeactivated is NOT treated as a non-account here (unlike isDeleted):
  // a deactivated user still exists and still receives invoices, so their
  // billing contact is not misleading the way a deleted one is.
  const activeAccountEmails = new Set(
    (users ?? [])
      .filter((user) => !user.isDeleted)
      .map((user) => normalize(user.email)),
  );
  const deletedAccountEmails = new Set(
    (users ?? [])
      .filter((user) => user.isDeleted)
      .map((user) => normalize(user.email)),
  );
  const primaryEmail = normalize(billingAddress);

  return (
    <div className="tables-wrapper" style={{ padding: '24px' }}>
      <Flex justify="space-between" align="center" style={{ marginBottom: 16 }}>
        <Typography.Title level={4} style={{ margin: 0 }}>
          Billing Contacts
        </Typography.Title>
        <Typography.Text style={{ color: '#9FA1AD', fontWeight: 500 }}>
          Read-only
        </Typography.Text>
      </Flex>

      {isLoading ? (
        <Typography.Text style={{ color: '#9FA1AD', fontSize: 14 }}>
          Loading…
        </Typography.Text>
      ) : isError ? (
        <Typography.Text style={{ color: '#D74846', fontSize: 14 }}>
          Couldn&apos;t load billing contacts. Try refreshing the page.
        </Typography.Text>
      ) : !contacts.length ? (
        <Typography.Text style={{ color: '#2b333c', fontSize: 14 }}>
          None set
        </Typography.Text>
      ) : (
        <div>
          {contacts.map((contact) => {
            const normalized = normalize(contact);
            const isPrimary = !!billingAddress && normalized === primaryEmail;
            const isDeletedAccount = deletedAccountEmails.has(normalized);
            const isNotAnAccount =
              !activeAccountEmails.has(normalized) && !isDeletedAccount;

            return (
              <div
                key={contact}
                data-testid="billing-contact-row"
                className="billing-contact-row"
              >
                <div className="billing-contact-email">
                  <MailOutlined />
                  {contact}
                  {isPrimary && (
                    <Tag color="gold" bordered={false} icon={<StarFilled />}>
                      Primary
                    </Tag>
                  )}
                  {isPrimary && stripeBillingEmailSynced === false && (
                    <Tag color="blue" bordered={false} icon={<SyncOutlined />}>
                      Stripe sync pending
                    </Tag>
                  )}
                  {/* No billingContacts/billingAddress set: invoices go to
                      the user who activated the franchise. */}
                  {isOwnerFallback && (
                    <Tag color="default" bordered={false} icon={<InfoCircleOutlined />}>
                      Account owner fallback
                    </Tag>
                  )}
                </div>
                {(isNotAnAccount || isDeletedAccount) && (
                  <div className="billing-contact-tags">
                    {isNotAnAccount && (
                      <Tag color="error" bordered={false} icon={<WarningFilled />}>
                        Not a franchise account
                      </Tag>
                    )}
                    {isDeletedAccount && (
                      <Tag color="orange" bordered={false} icon={<WarningFilled />}>
                        Account deleted
                      </Tag>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
